import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Stack,
  Link,
  useTheme,
} from "@mui/material";
import { AddCircleOutline, CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { MandatoryWorkshop, TraineeProgressOverviewDto } from "../../../types/traineesTypes";
import { Link as RouterLink } from "react-router-dom";

// the names for the IP milestones
const ipMilestones = ["Angebot bei EV", "AP gehalten und abgegeben", "DL abgegeben"];

interface DialogProps {
  open: boolean;
  onClose: () => void;
  trainee: TraineeProgressOverviewDto | undefined;
  admitTrainee: (memberID: number) => void;
}

/**
 * The AdmissionDialog component displays a dialog to confirm the admission of a trainee
 * @param open - boolean if the dialog is open
 * @param onClose - function to close the dialog
 * @param trainee - the trainee to be admitted
 * @param admitTrainee - (memberID: number) => void
 * @returns the dialog to confirm the admission of a trainee
 */
const AdmissionDialog: React.FunctionComponent<DialogProps> = ({ open, onClose, trainee, admitTrainee }) => {
  const handleAdmission = () => {
    if (trainee) {
      admitTrainee(trainee.memberID);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Aufnahme</DialogTitle>
      <DialogContent>
        {trainee?.firstname} {trainee?.lastname} aufnehmen?
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Abbrechen
        </Button>
        <Button onClick={handleAdmission} variant="contained">
          Aufnehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
interface Props {
  trainees: TraineeProgressOverviewDto[];
  admitTrainee: (memberID: number) => void;
  mandatoryWorkshops: MandatoryWorkshop[];
}

/**
 * The TraineeSectionTable component displays the table to display the trainees of a generation and all checklists
 * @param trainees - Array of TraineeProgressOverviewDto
 * @param admitTrainee - function to admit a trainee
 * @returns the table to display the trainees of a generation
 */
const TraineeSectionTable: React.FunctionComponent<Props> = (props: Props) => {
  const theme = useTheme();

  const styles = {
    tableHeader: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      textAlign: "center",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
  };
  const [openAdmissionDialog, setOpenAdmissionDialog] = React.useState(false);
  const [selectedTrainee, setSelectedTrainee] = React.useState<TraineeProgressOverviewDto | undefined>();
  const { trainees, admitTrainee, mandatoryWorkshops } = props;

  const handleAdmission = (trainee: TraineeProgressOverviewDto) => {
    setOpenAdmissionDialog(true);
    setSelectedTrainee(trainee);
  };

  // Check if all booleans are true and all dates are set
  function checkTraineeProgress(trainee: TraineeProgressOverviewDto): boolean {
    // Check if for all mandatory workshops feedback is given
    const feedbackGivenForAllWorkshops = mandatoryWorkshops.every((workshop) =>
      trainee.mandatoryWorkshopFeedback.some(
        (feedback) => feedback.workshopId === workshop.workshopId && feedback.feedbackGiven
      )
    );
    return (
      trainee.offerAtEv &&
      trainee.zpHeld &&
      trainee.apAtEv &&
      trainee.apHeld &&
      trainee.dlAtEv &&
      feedbackGivenForAllWorkshops
    );
  }

  return (
    <>
      <AdmissionDialog
        open={openAdmissionDialog}
        onClose={() => setOpenAdmissionDialog(false)}
        trainee={selectedTrainee}
        admitTrainee={admitTrainee}
      />
      <Table size="small" component={Paper} sx={{ maxWidth: 1200 }}>
        <TableHead sx={styles.tableHeader}>
          <TableRow sx={{ height: 300, verticalAlign: "bottom" }}>
            <TableCell sx={{ minWidth: 130, width: 150, border: 1, borderColor: "#fff" }}>
              <Typography color={"white"} fontWeight={"bold"}>
                Trainee
              </Typography>
            </TableCell>
            {ipMilestones.map((milestone) => (
              <TableCell sx={{ overflow: "hidden", border: 1, borderColor: "#fff" }} key={milestone}>
                <Box>
                  <Typography sx={{ color: "white", mb: 1 }} fontWeight="regular">
                    {milestone}
                  </Typography>
                </Box>
              </TableCell>
            ))}
            {mandatoryWorkshops.map((mandatoryWorkshop) => (
              <TableCell sx={{ overflow: "hidden", border: 1, borderColor: "#fff" }} key={mandatoryWorkshop.workshopId}>
                <Box>
                  <Typography sx={{ color: "white", mb: 1 }} fontWeight="regular">
                    {mandatoryWorkshop.workshopName}
                  </Typography>
                </Box>
              </TableCell>
            ))}
            <TableCell sx={{ maxWidth: 80, width: 60, border: 1, borderColor: "#fff" }}>
              <Typography color={"white"} fontWeight={"regular"}>
                Aufnehmen
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trainees.map((trainee, index) => (
            <TableRow key={trainee.memberID} sx={{ backgroundColor: index % 2 === 0 ? "#f7f7f7" : "#e4e4e4" }}>
              <TableCell sx={{ width: 150, border: 1, borderColor: "#fff" }}>
                <Link
                  component={RouterLink}
                  to={`/gesamtuebersicht/${trainee.memberID}`}
                  underline="none"
                  color="inherit"
                  sx={{ fontWeight: "bold" }}
                >
                  {trainee.firstname} {trainee.lastname}
                </Link>
              </TableCell>

              <TableCell
                align="center"
                sx={{
                  width: 10,
                  border: 1,
                  borderColor: "#fff",
                  padding: 0,
                }}
              >
                {trainee.offerAtEv ? <CheckBox color="primary" /> : <CheckBoxOutlineBlank color="primary" />}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  width: 10,
                  border: 1,
                  borderColor: "#fff",
                  padding: 0,
                }}
              >
                {trainee.apAtEv && trainee.apHeld ? (
                  <CheckBox color="primary" />
                ) : (
                  <CheckBoxOutlineBlank color="primary" />
                )}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  width: 10,
                  border: 1,
                  borderColor: "#fff",
                  padding: 0,
                }}
              >
                {trainee.dlAtEv ? <CheckBox color="primary" /> : <CheckBoxOutlineBlank color="primary" />}
              </TableCell>
              {mandatoryWorkshops.map((mandatoryWorkshop) => {
                const feedback = trainee.mandatoryWorkshopFeedback.find(
                  (feedback) => feedback.workshopId === mandatoryWorkshop.workshopId
                );
                return (
                  <TableCell
                    key={`${trainee.memberID}-mandatory-${mandatoryWorkshop.workshopId}`}
                    align="center"
                    sx={{
                      width: 10,
                      border: 1,
                      borderColor: "#fff",
                      padding: 0,
                    }}
                  >
                    {feedback?.feedbackGiven ? <CheckBox color="primary" /> : <CheckBoxOutlineBlank color="primary" />}
                  </TableCell>
                );
              })}
              <TableCell sx={{ maxWidth: 100, width: 80, border: 1, borderColor: "#fff" }} align="right">
                {checkTraineeProgress(trainee) ? (
                  trainee.memberStatus?.name === "Trainee" ? (
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      spacing={1}
                      component={Button}
                      onClick={() => handleAdmission(trainee)}
                    >
                      <Typography fontWeight={"bold"} fontSize={12} color={"black"}>
                        Aufnehmen
                      </Typography>
                      <AddCircleOutline color="primary" fontSize="small" />
                    </Stack>
                  ) : trainee.memberStatus?.name === "aktives Mitglied" ? (
                    <Typography fontWeight={"bold"} fontSize={12} color={"black"} textAlign={"center"}>
                      Aufgenommen
                    </Typography>
                  ) : null
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default TraineeSectionTable;
